import React, { useState, useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { getPageTransitionProps } from "../../utils/pageTransition";
import Nav from "./Nav";
import { Link, useNavigate } from "react-router-dom";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { useActiveDomain, DOMAIN_PR } from "../../utils/domain";
import { MdTimer } from "react-icons/md";
import { FiSettings, FiKey, FiAlertCircle, FiZap, FiInfo } from "react-icons/fi";

import NotebookBackground from "../../img/NotebookBackground";
import deltaLogo from '../../img/logo_full.png';
import ContactModal from "./ContactModal";
import AboutMatematlyModal from "./AboutMatematlyModal";

// --- SUB-KOMPONENT LICZNIKA ---
// Czas liczony już w pierwszym renderze i bez animacji wejścia: nagłówek montuje się
// na nowo przy każdym ekranie, więc licznik "wskakiwał" przy każdym przejściu.
const formatTimeLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = expiresAt - Math.floor(Date.now() / 1000);
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h}:${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
};

export const PromoTimer = ({ expiresAt, onClick }) => {
  const [timeLeft, setTimeLeft] = useState(() => formatTimeLeft(expiresAt));

  useEffect(() => {
    setTimeLeft(formatTimeLeft(expiresAt));
    if (!formatTimeLeft(expiresAt)) return;

    const interval = setInterval(() => {
      const next = formatTimeLeft(expiresAt);
      setTimeLeft(next);
      if (!next) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!timeLeft) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5   text-[#233855] px-3 py-1.5 rounded-full  hover:bg-[#233855]/10 transition-colors mr-2"
    >
      <MdTimer className="text-sm" />
      <span className="text-sm font-bold font-apotek tracking-wider pt-0.5">
        {timeLeft}
      </span>
    </button>
  );
};

export const readStoredStatus = () => {
  try {
    return JSON.parse(localStorage.getItem('userStatus'));
  } catch {
    return null;
  }
};

const Page = ({ showNav, children, accountStatus, dailyGoalProgress, disableExit = false }) => {  const isLoggedIn = !!localStorage.getItem('token');

  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalType, setContactModalType] = useState(null);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const [localAccountStatus, setLocalAccountStatus] = useState(readStoredStatus);
  const [headerHidden, setHeaderHidden] = useState(false);

  const chatMenuRef = useRef(null);
  const navigate = useNavigate();

  const prefersReducedMotion = useReducedMotion();
  const transitionProps = getPageTransitionProps(prefersReducedMotion, disableExit);

  const { paddingTop, paddingBottom } = useAndroidSafeArea();

  const headerHeight = `calc(3rem + ${paddingTop})`;
  const footerPadding = paddingBottom;
  const mainPaddingBottom = showNav 
    ? `calc(4rem + ${paddingBottom})` 
    : paddingBottom;

  useEffect(() => {
    if (!accountStatus) setLocalAccountStatus(readStoredStatus());
  }, [accountStatus]);

  const activeStatus = accountStatus || localAccountStatus;
  const activeDomain = useActiveDomain();

  // W trialu "Przedłuż dostęp" nie mówi, że to cennik. Trial = dostęp kończy się
  // w ciągu 72h (ta sama reguła co TrialTimer); kupujący mają dostęp do matury.
  const nowTs = Date.now() / 1000;
  const isPromoActive = (activeStatus?.promo_offer_expires_at || 0) > nowTs;
  const accessLeft = (activeStatus?.access_expires_at_timestamp || 0) - nowTs;
  const isInTrial = accessLeft > 0 && accessLeft <= 72 * 3600;
  const upgradeLabel = isPromoActive ? "Cennik · dziś taniej" : isInTrial ? "Cennik" : "Przedłuż dostęp";

  useEffect(() => {
    const onOpen = () => setHeaderHidden(true);
    const onClose = () => setHeaderHidden(false);
    window.addEventListener('bottomsheet:open', onOpen);
    window.addEventListener('bottomsheet:close', onClose);
    return () => {
      window.removeEventListener('bottomsheet:open', onOpen);
      window.removeEventListener('bottomsheet:close', onClose);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setIsChatMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openContactModal = (type) => {
    setContactModalType(type);
    setIsContactModalOpen(true);
    setIsChatMenuOpen(false);
  };

  const handleAboutClick = () => {
    setIsAboutModalOpen(true);
    setIsChatMenuOpen(false);
  };

  const handleUpgradeClick = () => {
    navigate('/upgrade');
    setIsChatMenuOpen(false);
  };

  return (
    <div className={`relative min-h-[100dvh] w-full flex flex-col ${!showNav ? 'bg-white lg:bg-transparent' : ''}`}>
      {!showNav ? (
        <div className="hidden lg:block"><NotebookBackground /></div>
      ) : (
        <NotebookBackground />
      )}

      {/* Białe tło + header wewnątrz main — gwarantuje że portal modal (fixed z-[9990+])
          zawsze przykryje header, bo main ma z-0 a portal jest w root context powyżej. */}
      <main
        className={`relative z-0 flex flex-col flex-1 overflow-y-auto ${showNav ? 'bg-white lg:bg-transparent' : ''}`}
        style={{ paddingBottom: mainPaddingBottom }}
      >
        <header
          className={`w-full flex-shrink-0 lg:sticky lg:top-0 lg:z-10 ${showNav ? 'bg-white lg:shadow-[0_4px_15px_rgba(0,0,0,0.40)]' : 'bg-transparent'}`}
          style={{ pointerEvents: headerHidden ? 'none' : undefined }}
        >
            <div className="max-w-[90rem] mx-auto px-4 lg:px-6">
                <div
                  className="relative flex justify-between items-center"
                  style={{
                      paddingTop: paddingTop,
                      height: headerHeight
                  }}
                >
                  {/* LEWA STRONA */}
                  <div className="flex items-center gap-4">
                    <Link to="/app" className="flex items-center gap-2 flex-shrink-0">
                      <img src={deltaLogo} alt="Matemaly logo" className="h-9 w-9" />
                      <span className="font-apotek text-xl text-[#233855] tracking-wider">MATEMATLY</span>
                      {activeDomain === DOMAIN_PR && (
                        <span className="font-apotek text-xl text-[#E09F2E] tracking-wider">(PR)</span>
                      )}
                    </Link>
                  </div>

                  {/* ŚRODEK */}
                  {showNav && ( <div className="hidden xl:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md"> <Nav /> </div> )}

                  {/* PRAWA STRONA */}
                  <div className="flex items-center">

                    {/* --- LICZNIK OFERTY --- */}
                    {activeStatus?.promo_offer_expires_at && (
                        <PromoTimer
                            expiresAt={activeStatus.promo_offer_expires_at}
                            onClick={() => navigate('/upgrade')}
                        />
                    )}

                    {/* CZAT MENU */}
                    <div className="relative" ref={chatMenuRef}>
                      <button
                        onClick={() => setIsChatMenuOpen(prev => !prev)}
                        aria-label="Ustawienia i menu"
                        className="transition-opacity hover:opacity-80 flex-shrink-0 flex items-center justify-center p-1"
                      >
                        <FiSettings className="h-6 w-6 text-[#233855]" />
                      </button>

                      {isChatMenuOpen && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-20 border border-gray-100 overflow-hidden">
                          <div className="py-1.5">

                            {isLoggedIn && (
                              <>
                                <button onClick={() => { navigate('/settings'); setIsChatMenuOpen(false); }} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#233855] hover:bg-gray-50 transition-colors">
                                  <FiSettings size={15} className="text-[#233855] flex-shrink-0" />
                                  Ustawienia
                                </button>
                                <button onClick={handleUpgradeClick} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#233855] hover:bg-gray-50 transition-colors">
                                  <FiKey size={15} className="text-[#E09F2E] flex-shrink-0" />
                                  {upgradeLabel}
                                </button>
                                <hr className="my-1 border-t border-gray-100" />
                              </>
                            )}

                            <button onClick={() => openContactModal('page-issue')} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                              <FiAlertCircle size={15} className="text-gray-400 flex-shrink-0" />
                              Zgłoś problem
                            </button>

                            <button onClick={() => openContactModal('suggestion')} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                              <FiZap size={15} className="text-gray-400 flex-shrink-0" />
                              Zaproponuj ulepszenie
                            </button>

                            <hr className="my-1 border-t border-gray-100" />

                            <button onClick={handleAboutClick} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                              <FiInfo size={15} className="text-gray-400 flex-shrink-0" />
                              Więcej o Matematly
                            </button>

                          </div>
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
        </header>

<motion.div
          {...transitionProps}
          className="flex flex-col flex-1"
        >
          {children}
        </motion.div>
      </main>

      {/* Dolna nawigacja */}
      {showNav && (
        <div 
          className="block xl:hidden fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.40)] z-50"
          style={{
            paddingBottom: footerPadding
          }}
        >
          <Nav />
        </div>
      )}

      {isContactModalOpen && (
        <ContactModal
          onClose={() => setIsContactModalOpen(false)}
          type={contactModalType}
        />
      )}
      {isAboutModalOpen && (
        <AboutMatematlyModal
            onClose={() => setIsAboutModalOpen(false)}
        />
      )}

    </div>
  );
};

export default Page;